import { useEffect, useState } from 'react';
import { Address, PlacesServiceStatusTypes, Suggestion } from './types';
import * as googleMapsUtils from './googleMapsUtils';

export const TranslationKeys = {
  exampleAddressText: 'address_input_design_sample_address',
  apiDisconnectedPreviewText: 'address_input_states_error_feature_setup',
  apiDisconnectedLiveSiteText:
    'address_input_states_viewer_error_feature_setup',
  overQuota: 'address_input_states_error_quota',
  noResults: 'address_input_states_no_results',
  generalError: 'address_input_states_error_general',
};

const PlacesServiceStatusDescriptionsMap: { [statusType: string]: string } = {
  [PlacesServiceStatusTypes.NotFound]: TranslationKeys.noResults,
  [PlacesServiceStatusTypes.ZeroResults]: TranslationKeys.noResults,
  [PlacesServiceStatusTypes.InvalidRequest]: TranslationKeys.generalError,
  [PlacesServiceStatusTypes.OverQueryLimit]: TranslationKeys.generalError,
  [PlacesServiceStatusTypes.RequestDenied]:
    TranslationKeys.apiDisconnectedLiveSiteText,
  [PlacesServiceStatusTypes.UnknownError]: TranslationKeys.generalError,
};

const PlacesServiceStatusDescriptionsMapFallback: {
  [statusType: string]: string;
} = {
  [PlacesServiceStatusTypes.NotFound]: 'The place referenced was not found.',
  [PlacesServiceStatusTypes.InvalidRequest]: 'This request was invalid.',
  [PlacesServiceStatusTypes.OverQueryLimit]:
    'The application has gone over its request quota.',
  [PlacesServiceStatusTypes.RequestDenied]:
    'The application is not allowed to use the PlacesService.',
  [PlacesServiceStatusTypes.UnknownError]:
    'The PlacesService request could not be processed due to a server error. The request may succeed if you try again.',
  [PlacesServiceStatusTypes.ZeroResults]:
    'No result was found for this request.',
};

// todo: test hook
export const useGoogleMapsAutoComplete = (
  apiKey: string,
  lang: string,
  countryCode: string | undefined,
  translate: (key: string, fallbackValue: string) => string = (key: string) =>
    key,
) => {
  const [autoCompleteResults, setAutoCompleteResults] = useState<{
    requestId: number;
    results: Array<Address>;
  } | null>(null);

  const [suggestions, setSuggestions] = useState<Array<Suggestion>>([]);
  const [lastRequestId, setLastRequestId] = useState(0);
  const [autocompleteError, setAutocompleteError] = useState<boolean>(false);

  const clearSuggestions = () => setSuggestions([]);

  const autoComplete = async (value: string) => {
    const requestId = lastRequestId + 1;
    setLastRequestId(requestId);

    const request: any = { input: value };
    if (typeof countryCode === 'string') {
      request.componentRestrictions = { country: countryCode.toLowerCase() };
    }
    try {
      const results = await googleMapsUtils.autocomplete(apiKey, lang, request);
      setAutocompleteError(false);
      setAutoCompleteResults({ requestId, results });
    } catch (errorStatus) {
      setAutocompleteError(true);
      setAutoCompleteResults({
        requestId,
        results: [
          {
            place_id: '',
            description: translate(
              PlacesServiceStatusDescriptionsMap[errorStatus],
              PlacesServiceStatusDescriptionsMapFallback[errorStatus],
            ),
            types: [],
          },
        ],
      });
    }
  };

  useEffect(() => {
    if (
      autoCompleteResults &&
      autoCompleteResults.requestId === lastRequestId
    ) {
      setSuggestions(
        autoCompleteResults.results.map(result => {
          return { id: result.place_id, value: result.description };
        }),
      );
    }
  }, [autoCompleteResults, lastRequestId]);

  return { autoComplete, autocompleteError, suggestions, clearSuggestions };
};
